import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { SlidersHorizontal, X, ChevronRight, MapPin, List, Map as MapIcon } from "lucide-react";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SearchWidget from "@/components/SearchWidget";
import HotelCard from "@/components/HotelCard";
import HotelsMapView from "@/components/HotelsMapView";
import AdSlot from "@/components/AdSlot";
import { track } from "@/lib/analytics";

const CONCEPTS = ["Ultra Her Şey Dahil", "Her Şey Dahil", "Yarım Pansiyon", "Tam Pansiyon", "Sadece Oda Kahvaltı"];
const AMENITIES = ["Açık Havuz", "Spa & Wellness", "Özel Plaj", "Aquapark", "Çocuk Kulübü", "Fitness Merkezi", "Ücretsiz Wi-Fi", "Havalimanı Transferi"];

export default function SearchResultsPage() {
    const [params, setParams] = useSearchParams();
    const [hotels, setHotels] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [filterOpen, setFilterOpen] = useState(false);
    const [networkError, setNetworkError] = useState(false);

    // filter state
    const destination = params.get("destination") || "";
    const searchQ = params.get("q") || "";
    const [stars, setStars] = useState([]);
    const [concept, setConcept] = useState("");
    const [amenities, setAmenities] = useState([]);
    const [minPrice, setMinPrice] = useState(0);
    const [maxPrice, setMaxPrice] = useState(20000);
    const [minRating, setMinRating] = useState(0);
    const [sort, setSort] = useState("recommended");
    const [view, setView] = useState("list"); // 'list' or 'map'
    const [bbox, setBbox] = useState(null);  // {minLat,minLng,maxLat,maxLng} — overrides destination when set
    const [areaSearching, setAreaSearching] = useState(false);
    const [availableCount, setAvailableCount] = useState(null);
    const [destContext, setDestContext] = useState(null); // breadcrumb + children + siblings

    // Fetch destination context (breadcrumb + sub-destinations chips)
    useEffect(() => {
        if (!destination) { setDestContext(null); return; }
        api.get(`/destinations/${destination}/context`)
            .then((r) => setDestContext(r.data))
            .catch(() => setDestContext(null));
    }, [destination]);

    const initial = useMemo(() => ({
        q: searchQ,
        destination,
        check_in: params.get("check_in"),
        check_out: params.get("check_out"),
        adults: Number(params.get("adults") || 2),
        children: Number(params.get("children") || 0),
        rooms: Number(params.get("rooms") || 1),
    }), [params]);

    useEffect(() => {
        setLoading(true);
        const q = new URLSearchParams();
        if (bbox) {
            q.set("bbox", `${bbox.minLat},${bbox.minLng},${bbox.maxLat},${bbox.maxLng}`);
        } else if (destination) {
            q.set("destination", destination);
        }
        if (searchQ) q.set("q", searchQ);
        if (stars.length) q.set("stars", stars.join(","));
        if (concept) q.set("concept", concept);
        if (amenities.length) q.set("amenities", amenities.join(","));
        if (minPrice > 0) q.set("min_price", minPrice);
        if (maxPrice < 20000) q.set("max_price", maxPrice);
        if (minRating > 0) q.set("min_rating", minRating);
        q.set("sort", sort);
        q.set("limit", 50);
        if (initial.check_in && initial.check_out) {
            q.set("check_in", initial.check_in);
            q.set("check_out", initial.check_out);
            q.set("adults", initial.adults);
            q.set("rooms", initial.rooms);
        }

        api.get(`/hotels?${q.toString()}`)
            .then((r) => {
                setHotels(r.data.items);
                setTotal(r.data.total);
                setAvailableCount(r.data.available_count ?? null);
                setNetworkError(false);
                // Track search event (once per query change)
                const query_parts = [];
                if (searchQ) query_parts.push(searchQ);
                if (destination) query_parts.push(destination);
                if (query_parts.length) {
                    track("search", {
                        search_query: query_parts.join(" "),
                        destination_slug: destination || "",
                    });
                }
            })
            .catch(() => { setHotels([]); setTotal(0); setNetworkError(true); })
            .finally(() => { setLoading(false); setAreaSearching(false); });
    }, [destination, searchQ, stars, concept, amenities, minPrice, maxPrice, minRating, sort, bbox, initial]);

    // Reset bbox when destination changes (user clicked a different city)
    useEffect(() => { setBbox(null); }, [destination]);

    const handleSearchArea = (newBounds) => {
        setAreaSearching(true);
        setBbox(newBounds);
    };
    const clearAreaSearch = () => setBbox(null);

    const toggleStar = (s) => setStars((p) => p.includes(s) ? p.filter((x) => x !== s) : [...p, s]);
    const toggleAmenity = (a) => setAmenities((p) => p.includes(a) ? p.filter((x) => x !== a) : [...p, a]);
    const clearFilters = () => { setStars([]); setConcept(""); setAmenities([]); setMinPrice(0); setMaxPrice(20000); setMinRating(0); };

    const FilterPanel = () => (
        <div data-testid="filters-panel" className="space-y-6">
            <div className="flex items-center justify-between">
                <h3 className="font-display text-lg font-bold">Filtreler</h3>
                <button data-testid="clear-filters" onClick={clearFilters} className="text-xs font-semibold text-sky-500 hover:underline">Temizle</button>
            </div>

            <div>
                <label className="block text-sm font-semibold text-slate-700 dark:text-slate-200 mb-2">Fiyat aralığı (TRY/gece)</label>
                <div className="flex gap-2 items-center">
                    <input data-testid="filter-min-price" type="number" min="0" value={minPrice} onChange={(e) => setMinPrice(Number(e.target.value))} placeholder="Min" className="w-full px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm" />
                    <span className="text-slate-400">—</span>
                    <input data-testid="filter-max-price" type="number" min="0" value={maxPrice} onChange={(e) => setMaxPrice(Number(e.target.value))} placeholder="Max" className="w-full px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm" />
                </div>
            </div>

            <div>
                <label className="block text-sm font-semibold text-slate-700 dark:text-slate-200 mb-2">Yıldız</label>
                <div className="space-y-2">
                    {[5, 4, 3].map((s) => (
                        <label key={s} className="flex items-center gap-2 cursor-pointer">
                            <input data-testid={`filter-star-${s}`} type="checkbox" checked={stars.includes(s)} onChange={() => toggleStar(s)} className="accent-sky-500 h-4 w-4" />
                            <span className="text-sm text-slate-700 dark:text-slate-200">{s} yıldız</span>
                        </label>
                    ))}
                </div>
            </div>

            <div>
                <label className="block text-sm font-semibold text-slate-700 dark:text-slate-200 mb-2">Puan</label>
                <select data-testid="filter-rating" value={minRating} onChange={(e) => setMinRating(Number(e.target.value))} className="w-full px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm">
                    <option value="0">Tümü</option>
                    <option value="7">7+ İyi</option>
                    <option value="8">8+ Çok İyi</option>
                    <option value="9">9+ Mükemmel</option>
                </select>
            </div>

            <div>
                <label className="block text-sm font-semibold text-slate-700 dark:text-slate-200 mb-2">Konsept</label>
                <select data-testid="filter-concept" value={concept} onChange={(e) => setConcept(e.target.value)} className="w-full px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm">
                    <option value="">Tümü</option>
                    {CONCEPTS.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
            </div>

            <div>
                <label className="block text-sm font-semibold text-slate-700 dark:text-slate-200 mb-2">Tesis Özellikleri</label>
                <div className="space-y-2">
                    {AMENITIES.map((a) => (
                        <label key={a} className="flex items-center gap-2 cursor-pointer">
                            <input data-testid={`filter-amenity-${a.replace(/\s/g, '-')}`} type="checkbox" checked={amenities.includes(a)} onChange={() => toggleAmenity(a)} className="accent-sky-500 h-4 w-4" />
                            <span className="text-sm text-slate-700 dark:text-slate-200">{a}</span>
                        </label>
                    ))}
                </div>
            </div>
        </div>
    );

    return (
        <div data-testid="search-page" className="bg-slate-50 dark:bg-slate-950 min-h-screen">
            <Navbar />
            {/* Sticky search */}
            <div className="sticky top-16 md:top-20 z-20 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 py-4">
                <div className="container mx-auto px-4 md:px-6 max-w-7xl">
                    <SearchWidget compact initial={initial} />
                </div>
            </div>

            <div className="container mx-auto px-4 md:px-6 max-w-7xl py-8">
                {/* Destination breadcrumb + sub-destination chips */}
                {destContext && (
                    <div className="mb-5 space-y-3" data-testid="dest-context">
                        {/* Breadcrumb */}
                        {destContext.breadcrumb?.length > 0 && (
                            <nav className="flex items-center gap-1 text-sm text-slate-500 flex-wrap">
                                {destContext.breadcrumb.map((b, i) => (
                                    <React.Fragment key={b.slug}>
                                        <Link
                                            to={`/destinasyon/${b.slug}`}
                                            className="hover:text-sky-600 transition-colors"
                                            data-testid={`bc-${b.slug}`}
                                        >{b.name}</Link>
                                        <ChevronRight className="h-3 w-3 opacity-40" />
                                    </React.Fragment>
                                ))}
                                <Link to={`/destinasyon/${destContext.current.slug}`} className="text-slate-900 dark:text-white font-semibold hover:underline">{destContext.current.name}</Link>
                            </nav>
                        )}

                        {/* Direct sub-destinations chips (e.g. Antalya → Side, Belek, Alanya...) */}
                        {destContext.children?.length > 0 && (
                            <div data-testid="sub-destinations">
                                <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2 flex items-center gap-1.5">
                                    <MapPin className="h-3 w-3 text-sky-500" /> {destContext.current.name} bölgeleri
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    {destContext.children.map((c) => (
                                        <Link
                                            key={c.slug}
                                            to={`/search?destination=${c.slug}`}
                                            data-testid={`sub-${c.slug}`}
                                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-sm hover:border-sky-400 hover:bg-sky-50 dark:hover:bg-sky-950/30 hover:text-sky-700 dark:hover:text-sky-300 transition-colors"
                                        >
                                            <span className="font-semibold">{c.name}</span>
                                            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400">{c.hotel_count}</span>
                                        </Link>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Siblings (if current is leaf, suggest peers) */}
                        {destContext.children?.length === 0 && destContext.siblings?.length > 0 && (
                            <div data-testid="sibling-destinations">
                                <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2 flex items-center gap-1.5">
                                    <MapPin className="h-3 w-3 text-sky-500" /> Çevredeki bölgeler
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    {destContext.siblings.slice(0, 10).map((s) => (
                                        <Link
                                            key={s.slug}
                                            to={`/search?destination=${s.slug}`}
                                            data-testid={`sib-${s.slug}`}
                                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-sm hover:border-sky-400 hover:bg-sky-50 dark:hover:bg-sky-950/30 transition-colors"
                                        >
                                            <span className="font-semibold">{s.name}</span>
                                            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400">{s.hotel_count}</span>
                                        </Link>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                <div className="flex items-center justify-between mb-6">
                    <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900 dark:text-white">
                        {loading ? "Aranıyor..." : availableCount != null ? `${availableCount} müsait / ${total} otel` : `${total} otel bulundu`}
                        {bbox ? <span className="text-sky-500"> · harita üzerinde</span> : destContext?.current?.name && <span className="text-sky-500"> · {destContext.current.name}</span>}
                        {!destContext && !bbox && destination && <span className="text-sky-500"> · {destination}</span>}
                        {searchQ && <span className="text-sky-500"> · &quot;{searchQ}&quot;</span>}
                    </h1>
                    <div className="flex items-center gap-2">
                        {/* View toggle */}
                        <div className="inline-flex items-center rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 p-0.5" data-testid="view-toggle">
                            <button
                                onClick={() => setView("list")}
                                data-testid="view-list"
                                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-colors ${view === "list" ? "bg-sky-500 text-white" : "text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"}`}
                            ><List className="h-3.5 w-3.5" /> Liste</button>
                            <button
                                onClick={() => setView("map")}
                                data-testid="view-map"
                                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-colors ${view === "map" ? "bg-sky-500 text-white" : "text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"}`}
                            ><MapIcon className="h-3.5 w-3.5" /> Harita</button>
                        </div>
                        <button data-testid="mobile-filter-toggle" onClick={() => setFilterOpen(true)} className="lg:hidden flex items-center gap-2 px-3 py-2 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-sm font-medium">
                            <SlidersHorizontal className="h-4 w-4" /> Filtrele
                        </button>
                        <select data-testid="sort-select" value={sort} onChange={(e) => setSort(e.target.value)} className="px-3 py-2 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-sm font-medium">
                            <option value="recommended">Önerilen</option>
                            <option value="price_asc">En düşük fiyat</option>
                            <option value="price_desc">En yüksek fiyat</option>
                            <option value="rating_desc">En yüksek puan</option>
                        </select>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
                    {/* Desktop filters */}
                    <aside className="hidden lg:block lg:col-span-1">
                        <div className="sticky top-44 p-5 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800">
                            <FilterPanel />
                        </div>
                    </aside>

                    {/* Mobile filters drawer */}
                    {filterOpen && (
                        <div className="lg:hidden fixed inset-0 z-50 bg-black/40 flex" onClick={() => setFilterOpen(false)}>
                            <div className="ml-auto w-full max-w-sm h-full bg-white dark:bg-slate-900 p-5 overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                                <div className="flex justify-end mb-2">
                                    <button onClick={() => setFilterOpen(false)} className="p-2 rounded-full hover:bg-slate-100"><X className="h-5 w-5" /></button>
                                </div>
                                <FilterPanel />
                            </div>
                        </div>
                    )}

                    {/* Results */}
                    <div className="lg:col-span-3 space-y-5">
                        {loading ? (
                            [...Array(4)].map((_, i) => (
                                <div key={i} className="h-64 rounded-2xl bg-slate-200 dark:bg-slate-800 animate-pulse" />
                            ))
                        ) : networkError ? (
                            <div data-testid="network-error" className="text-center py-20 px-6">
                                <div className="text-4xl mb-3">⚠️</div>
                                <div className="text-lg font-bold text-slate-900 dark:text-white mb-2">Bağlantı sorunu</div>
                                <p className="text-sm text-slate-500 max-w-md mx-auto mb-4">Otel listesi yüklenirken bağlantı hatası oluştu. İnternet bağlantınızı kontrol edip sayfayı yenileyin.</p>
                                <button
                                    onClick={() => window.location.reload()}
                                    data-testid="retry-search-btn"
                                    className="px-5 py-2 rounded-xl bg-sky-500 hover:bg-sky-600 text-white text-sm font-bold"
                                >
                                    Yeniden Dene
                                </button>
                            </div>
                        ) : hotels.length === 0 ? (
                            <div className="text-center py-20 text-slate-500">Aradığınız kriterlere uygun otel bulunamadı. Filtreleri değiştirip tekrar deneyin.</div>
                        ) : view === "map" ? (
                            <HotelsMapView hotels={hotels} onSearchArea={handleSearchArea} areaSearching={areaSearching} />
                        ) : (
                            hotels.map((h, idx) => (
                                <React.Fragment key={h.id}>
                                    <HotelCard hotel={h} horizontal />
                                    {idx === 4 && <AdSlot slot="search_results_inline" className="aspect-[16/3] my-2" />}
                                </React.Fragment>
                            ))
                        )}
                    </div>
                </div>
            </div>

            <Footer />
        </div>
    );
}
